import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import MovieIcon from '@mui/icons-material/MovieFilterOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';

export default function Welcome() {
  const { user } = useAuth();
  const t = useTranslation();
  if (!user) return null;
  const displayName = user.name || (user.email ? user.email.split('@')[0] : 'there');
  const initial = (user.name?.[0] ?? user.email?.[0] ?? '?').toUpperCase();

  return (
    <Box>
      <PageHeader
        title={`Welcome, ${displayName}`}
        description={t.welcome.scaffoldNote}
      />

      <Section title="Your account">
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 22 }}>
              {initial}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1">{user.name || user.email}</Typography>
              <Typography variant="body2">{user.email}</Typography>
            </Box>
            <Chip label="Active" color="success" size="small" />
          </Stack>
        </Paper>
      </Section>

      <Section title="Quick actions">
        <ActionCard
          icon={<MovieIcon />}
          title={t.minimaxH3.title}
          description={t.minimaxH3.description}
          to="/minimax-h3"
          ctaLabel={t.minimaxH3.generate}
        />
        <ActionCard
          icon={<HistoryIcon />}
          title={t.history.title}
          description={t.history.description}
          to="/history"
          ctaLabel="Open"
        />
      </Section>
    </Box>
  );
}

function ActionCard({
  icon,
  title,
  description,
  to,
  ctaLabel,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  to: string;
  ctaLabel: string;
}) {
  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" spacing={2.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            color: '#b9b0ff',
            backgroundColor: 'rgba(124, 111, 240, 0.14)',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Typography variant="body2" noWrap>
            {description}
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to={to}
          endIcon={<ArrowForwardIcon />}
          variant="outlined"
        >
          {ctaLabel}
        </Button>
      </Stack>
    </Paper>
  );
}
